import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Flame, Check, X, RefreshCw, Loader2, ExternalLink, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const inr = (n) => `\u20b9${Number(n || 0).toLocaleString('en-IN')}`;
const statusStyle = (s) => ({ Approved: 'bg-green-100 text-green-700', Rejected: 'bg-red-100 text-red-700', Pending: 'bg-amber-100 text-amber-700' }[s] || 'bg-slate-100 text-slate-600');

const AdsPaymentsManagement = () => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('Pending');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/ads-payments`, { params: status === 'all' ? {} : { status } });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) { toast({ title: 'Failed to load', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, newStatus) => {
    setBusy(id);
    try {
      await axios.patch(`${API}/admin/ads-payments/${id}`, { status: newStatus });
      toast({ title: `Marked ${newStatus}`, description: newStatus === 'Approved' ? 'Ad Wallet credited.' : '' });
      await load();
    } catch (e) { toast({ title: 'Action failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setBusy(''); }
  };

  const shown = items.filter((i) => !q || (i.name || '').toLowerCase().includes(q.toLowerCase()) || String(i.clientId || '').includes(q) || (i.email || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Flame className="w-6 h-6 text-orange-500" /> Ads / Fund Payments</h2>
          <p className="text-slate-500 text-sm">Verify client ad-fund payments. Approving credits their Ad Wallet.</p>
        </div>
        <Button onClick={load} variant="outline" size="sm"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        {['Pending', 'Approved', 'Rejected', 'all'].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-full text-sm font-medium ${status === s ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{s === 'all' ? 'All' : s}</button>
        ))}
        <div className="relative ml-auto"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / ID / email" className="pl-8 pr-3 py-2 rounded-lg border border-slate-200 text-sm outline-none" /></div>
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div> : shown.length === 0 ? (
        <div className="text-center py-16 text-slate-400">No requests found.</div>
      ) : (
        <div className="space-y-3">
          {shown.map((r) => (
            <div key={r.id} className="bg-white rounded-xl border border-slate-100 p-4 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[180px]">
                <div className="font-semibold text-slate-800">{r.name} <span className="text-xs text-slate-400">#{r.clientId}</span></div>
                <div className="text-xs text-slate-400">{r.email} &middot; {r.phone}</div>
                <div className="text-xs text-slate-500 mt-1">{r.purpose === 'onboarding_ads_budget' ? 'Onboarding Ads Budget' : 'Add Fund'} &middot; {new Date(r.createdAt).toLocaleString()}</div>
                {r.utr && <div className="text-xs text-slate-500">UTR: {r.utr}</div>}
              </div>
              <div className="text-2xl font-bold text-slate-800">{inr(r.amount)}</div>
              {r.screenshotPath && <a href={r.screenshotPath.startsWith('http') ? r.screenshotPath : `${BACKEND_URL}${r.screenshotPath}`} target="_blank" rel="noreferrer" className="text-blue-600 text-sm flex items-center gap-1">Screenshot <ExternalLink className="w-3 h-3" /></a>}
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${statusStyle(r.status)}`}>{r.status}</span>
              <div className="flex gap-2">
                {r.status !== 'Approved' && <Button size="sm" disabled={busy === r.id} onClick={() => act(r.id, 'Approved')} className="bg-green-600 hover:bg-green-700 text-white">{busy === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Approve</>}</Button>}
                {r.status !== 'Rejected' && <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => act(r.id, 'Rejected')} className="border-red-300 text-red-600"><X className="w-4 h-4 mr-1" /> Reject</Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdsPaymentsManagement;
